export enum CompanyDefaultStatementWarningType {
  ConclusionTemplating = "conclusionTemplating",
  IntroInvalidBraces = "introInvalidBraces",
  IntroUnrecognizedVariables = "introUnrecognizedVariables",
}

export interface CompanyDefaultStatementWarning {
  instances: string[];
  message: string;
  title: string;
  type: CompanyDefaultStatementWarningType;
}

const ALLOWED_STATEMENT_VARIABLES = new Set(["company_name", "job_title"]);
const WELL_FORMED_TEMPLATE_REGEX = /{{\s*([A-Za-z_][A-Za-z0-9_]*)\s*}}/g;

interface TemplateTokenMatch {
  end: number;
  name: string;
  start: number;
  token: string;
}

const getTemplateTokenMatches = (value: string): TemplateTokenMatch[] =>
  Array.from(value.matchAll(WELL_FORMED_TEMPLATE_REGEX)).map((match) => ({
    end: (match.index ?? 0) + match[0].length,
    name: match[1],
    start: match.index ?? 0,
    token: match[0],
  }));

const getRangeContainingIndex = (
  index: number,
  ranges: Pick<TemplateTokenMatch, "end" | "start">[],
) => ranges.find((range) => index >= range.start && index < range.end);

const getBraceChunk = (value: string, start: number): string => {
  let end = start + 1;

  while (end < value.length && !/\s/.test(value[end])) {
    end++;
  }

  return value.slice(start, end);
};

const findMalformedBraceInstances = (value: string): string[] => {
  const validTokenRanges = getTemplateTokenMatches(value);
  const instances: string[] = [];
  let index = 0;

  while (index < value.length) {
    const validRange = getRangeContainingIndex(index, validTokenRanges);

    if (validRange) {
      index = validRange.end;
      continue;
    }

    const character = value[index];
    if (character !== "{" && character !== "}") {
      index++;
      continue;
    }

    if (value.startsWith("{{", index)) {
      const closeIndex = value.indexOf("}}", index + 2);
      const nextOpenIndex = value.indexOf("{{", index + 2);

      if (
        closeIndex !== -1 &&
        (nextOpenIndex === -1 || nextOpenIndex > closeIndex)
      ) {
        instances.push(value.slice(index, closeIndex + 2));
        index = closeIndex + 2;
        continue;
      }
    }

    const chunk = getBraceChunk(value, index);
    instances.push(chunk);
    index += chunk.length;
  }

  return instances;
};

export const getDefaultOpeningStatementWarnings = (
  value: string,
): CompanyDefaultStatementWarning[] => {
  const warnings: CompanyDefaultStatementWarning[] = [];

  const unrecognizedVariables = getTemplateTokenMatches(value)
    .filter((match) => !ALLOWED_STATEMENT_VARIABLES.has(match.name))
    .map((match) => match.token);

  if (unrecognizedVariables.length > 0) {
    warnings.push({
      instances: unrecognizedVariables,
      message:
        "These will appear literally in the screener message. Check for typos or use a supported variable.",
      title: "Unrecognized variables in Default Intro",
      type: CompanyDefaultStatementWarningType.IntroUnrecognizedVariables,
    });
  }

  const malformedBraceInstances = findMalformedBraceInstances(value);

  if (malformedBraceInstances.length > 0) {
    warnings.push({
      instances: malformedBraceInstances,
      message:
        "Every variable must use a matched {{name}} pair. Stray braces will be sent as plain text.",
      title: "Unbalanced curly braces in Default Intro",
      type: CompanyDefaultStatementWarningType.IntroInvalidBraces,
    });
  }

  return warnings;
};

export const getDefaultClosingStatementWarnings = (
  value: string,
): CompanyDefaultStatementWarning[] => {
  const templateVariables = getTemplateTokenMatches(value).map(
    (match) => match.token,
  );
  const malformedBraceInstances = findMalformedBraceInstances(value);
  const templatingInstances = [
    ...templateVariables,
    ...malformedBraceInstances,
  ];

  if (templatingInstances.length === 0) {
    return [];
  }

  return [
    {
      instances: templatingInstances,
      message:
        "Default Conclusion is sent exactly as written. Variables are not substituted here, so curly-brace placeholders will appear literally.",
      title: "Review variables in Default Conclusion",
      type: CompanyDefaultStatementWarningType.ConclusionTemplating,
    },
  ];
};
